import { type FC, useEffect, useRef, useState } from 'react';
import { EarthLock, Loader2, Send } from 'lucide-react';
import {
  handleCommentEditorKeyDown,
  handleCommentPaste,
  insertTextAtCursor,
  normalizeCommentBody,
  resetCommentEditor,
  syncCommentDraftFromElement,
  syncCommentEditorValueIfBlurred,
} from '@/lib/comments';
import { CommentCharacterCount } from '@/components/inspector/comments/comment-character-count';
import { useCreateNodeCommentMutation } from '@/hooks/comments/useCreateNodeCommentMutation';
import { EmojiPickerComponent } from '@/components/emoji-picker-component';
import { useShareConfig } from '@/context/ShareConfigContext';
import { useCapabilities } from '@/hooks/useCapabilities';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export const CommentsComposer: FC<{
  nodeId: string;
  isComposerDisabled: boolean;
}> = ({ nodeId, isComposerDisabled }) => {
  const [isComposerFocused, setIsComposerFocused] = useState(false);
  const [composerDraft, setComposerDraft] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);

  const composerRef = useRef<HTMLDivElement>(null);
  const previousNodeIdRef = useRef<string | undefined>(nodeId);

  const tComments = useTranslations('inspector.comments');
  const shareConfig = useShareConfig();
  const { canCommentPrivately } = useCapabilities();

  const normalizedDraft = normalizeCommentBody(composerDraft);
  const isDraftEmpty = normalizedDraft.trim().length === 0;
  const showPlaceholder = !isComposerFocused && normalizedDraft.length === 0;

  const { createNodeCommentMutation: createCommentMutation } = useCreateNodeCommentMutation();

  const canSubmit = !isComposerDisabled && !isDraftEmpty && !createCommentMutation.isPending;

  const handleSubmit = () => {
    if (!canSubmit || !nodeId) {
      return;
    }

    createCommentMutation.mutate(
      {
        nodeId: nodeId,
        body: normalizedDraft,
        private: isPrivate,
      },
      {
        onSuccess: (response) => {
          resetCommentEditor({
            setDraft: setComposerDraft,
            editorRef: composerRef,
          });
          setIsPrivate(false);

          // Scroll to the newly created comment
          setTimeout(() => {
            const commentElement = document.getElementById(response.id);
            if (commentElement) {
              commentElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }, 100);
        },
      },
    );
  };

  useEffect(() => {
    if (nodeId === previousNodeIdRef.current) {
      return;
    }

    resetCommentEditor({
      setDraft: setComposerDraft,
      editorRef: composerRef,
    });

    previousNodeIdRef.current = nodeId;
  }, [nodeId, composerRef]);

  useEffect(() => {
    syncCommentEditorValueIfBlurred({
      editorRef: composerRef,
      nextValue: normalizedDraft,
    });
  }, [composerRef, normalizedDraft]);

  return (
    <div className={cn('flex max-h-67.5 flex-col duration-150', isPrivate ? 'bg-feature-sharing/20' : 'bg-ui-base')}>
      <div
        ref={composerRef}
        className={cn(
          'relative h-full w-full overflow-y-auto whitespace-pre-wrap px-3 pt-3 text-[13px] text-sm outline-hidden wrap-anywhere',
          "data-[placeholder-visible='true']:before:pointer-events-none data-[placeholder-visible='true']:before:absolute data-[placeholder-visible='true']:before:left-3 data-[placeholder-visible='true']:before:top-3 data-[placeholder-visible='true']:before:text-text-gray-alternate data-[placeholder-visible='true']:before:content-[attr(data-placeholder)]",
          isComposerDisabled && 'cursor-not-allowed opacity-60',
          normalizedDraft.length > 0 ? 'text-text-base' : 'text-text-gray-alternate',
        )}
        contentEditable={!isComposerDisabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder={tComments('addCommentPlaceholder')}
        data-placeholder-visible={showPlaceholder ? 'true' : 'false'}
        data-cy="comments-composer-input"
        onInput={(event) =>
          syncCommentDraftFromElement({
            element: event.currentTarget,
            setDraft: setComposerDraft,
          })
        }
        onPaste={(event) =>
          handleCommentPaste({
            event,
            setDraft: setComposerDraft,
          })
        }
        onKeyDown={(event) =>
          handleCommentEditorKeyDown({
            event,
            onSubmit: handleSubmit,
          })
        }
        onFocus={() => setIsComposerFocused(true)}
        onBlur={() => setIsComposerFocused(false)}
      />
      <div className="flex h-12 max-h-12 min-h-12 items-center justify-between pr-2 pl-1">
        <div className="flex items-center gap-0.5 px-2">
          {/* <ToolbarAction
            icon={AtSign}
            aria-label={tComments('mentionSomeone')}
            tooltip={tComments('mentionSomeone')}
            disabled
          /> */}
          <EmojiPickerComponent
            onEmojiSelect={(emoji) => {
              if (composerRef.current) {
                insertTextAtCursor(composerRef.current, emoji);
                syncCommentDraftFromElement({
                  element: composerRef.current,
                  setDraft: setComposerDraft,
                });
              }
            }}
            disabled={isComposerDisabled}
          />
          {canCommentPrivately && !shareConfig?.id && (
            <Button
              variant="main-toolbar"
              size="main-toolbar"
              icon={EarthLock}
              className="rounded-full"
              iconClassName={cn('icon-5', isPrivate ? 'stroke-feature-sharing!' : 'stroke-icon-base')}
              onClick={() => setIsPrivate(!isPrivate)}
              disabled={isComposerDisabled}
              tooltip={isPrivate ? tComments('makeCommentVisibleTooltip') : tComments('hideCommentTooltip')}
              data-cy="comments-composer-private-toggle"
            />
          )}
        </div>
        <div className="flex items-center gap-3">
          <CommentCharacterCount normalizedDraft={normalizedDraft} />
          <Button
            type="button"
            variant="main-toolbar"
            size="main-toolbar"
            className="rounded-xl p-5"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-cy="comments-submit-button"
            icon={createCommentMutation.isPending ? Loader2 : Send}
            iconClassName={cn(
              'icon-6',
              // Independent conditions, not branches of one: a private comment that is submitting
              // needs both the sharing colour and the spin, or its Loader2 renders frozen.
              isPrivate && 'stroke-feature-sharing!',
              createCommentMutation.isPending && 'animate-spin',
            )}
            tooltip={isPrivate ? tComments('submitPrivateComment') : tComments('submitComment')}
          />
        </div>
      </div>
    </div>
  );
};
